import React from 'react';
import { Box, Typography, useTheme, CircularProgress } from '@mui/material';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  Cell,
  LabelList
} from 'recharts';

const getBarColor = (value) => {
  if (value >= 100) return '#03C407';
  if (value >= 80) return '#FF9600';
  return '#FF5252';
};

const CustomTooltip = ({ active, payload, label }) => {
  const theme = useTheme();
  if (active && payload && payload.length) {
    return (
      <Box
        sx={{
          backgroundColor: theme.palette.background.paper,
          border: `1px solid ${theme.palette.divider}`,
          borderRadius: 1,
          p: 2,
          boxShadow: theme.shadows[4],
          color: theme.palette.text.primary
        }}
      >
        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: theme.palette.text.primary }}>
          {label}
        </Typography>
        {payload.map((entry, index) => (
          <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
            <Box
              sx={{
                width: 12,
                height: 12,
                borderRadius: '50%',
                backgroundColor: entry.color,
                flexShrink: 0
              }}
            />
            <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
              {entry.name}:{' '}
              <strong style={{ color: theme.palette.primary.main }}>
                {typeof entry.value === 'number' ? entry.value.toLocaleString('pt-BR') : entry.value}
              </strong>
            </Typography>
          </Box>
        ))}
      </Box>
    );
  }
  return null;
};

const RankingBarChart = ({ data = [], title = 'Ranking de Turnos', height = 400, loading = false }) => {
  const theme = useTheme();

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!data || data.length === 0) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height }}>
        <Typography color="text.secondary">Sem dados para exibir</Typography>
      </Box>
    );
  }

  const chartData = data.map((item, index) => {
    const position = index + 1;
    let medalIcon = '';
    if (position === 1) medalIcon = '🥇 ';
    else if (position === 2) medalIcon = '🥈 ';
    else if (position === 3) medalIcon = '🥉 ';
    
    return {
      rank: medalIcon || `${position}º `,
      position: position,
      equipe: item.des_equipe_cms || item.des_equipe || 'N/A',
      previsao: item.total_previsto || 0,
      realizado: item.total_aberto || item.total_realizado || 0,
      percentual: item.percentual_realizado || 0
    };
  });

  return (
    <Box sx={{ width: '100%', mb: 3 }}>
      <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, textAlign: 'center' }}>
        {title}
      </Typography>
      <ResponsiveContainer width="100%" height={height}>
        <BarChart
          data={chartData}
          layout="vertical"
          margin={{ top: 5, right: 50, left: 250, bottom: 5 }}
          barCategoryGap="30%"
        >
          <CartesianGrid strokeDasharray="3 3" stroke={theme.palette.divider} horizontal={false} />
          <XAxis
            type="number"
            tick={{ fill: theme.palette.text.secondary, fontSize: 12 }}
            stroke={theme.palette.divider}
            tickFormatter={(value) => value.toLocaleString('pt-BR')}
          />
          <YAxis
            type="category"
            dataKey="equipe"
            tick={{ fill: theme.palette.text.primary, fontSize: 11 }}
            stroke={theme.palette.divider}
            width={240}
          />
          <Tooltip content={<CustomTooltip />} />
          <Legend
            wrapperStyle={{ paddingTop: 10 }}
            formatter={(value) => (
              <span style={{ color: theme.palette.text.secondary, fontSize: 12 }}>{value}</span>
            )}
          />
          <Bar
            dataKey="previsao"
            name="Previsto"
            fill={theme.palette.secondary.main}
            radius={[0, 4, 4, 0]}
            barSize={16}
          >
            <LabelList
              dataKey="previsao"
              position="insideRight"
              formatter={(value) => value > 0 ? value.toLocaleString('pt-BR') : ''}
              style={{
                fill: '#FFFFFF',
                fontSize: 10,
                fontWeight: 600
              }}
            />
          </Bar>
          <Bar
            dataKey="realizado"
            name="Realizado"
            fill={theme.palette.primary.main}
            radius={[0, 4, 4, 0]}
            barSize={16}
          >
            <LabelList
              dataKey="realizado"
              position="insideRight"
              formatter={(value) => value > 0 ? value.toLocaleString('pt-BR') : ''}
              style={{
                fill: '#FFFFFF',
                fontSize: 10,
                fontWeight: 600
              }}
            />
          </Bar>
          <Bar
            dataKey="percentual"
            name="% Realizado"
            fill={theme.palette.info.main}
            radius={[0, 4, 4, 0]}
            barSize={14}
          >
            {chartData.map((entry, index) => (
              <Cell key={`cell-${index}`} fill={getBarColor(entry.percentual)} />
            ))}
            <LabelList
              dataKey="percentual"
              position="right"
              formatter={(value) => `${value.toFixed(1)}%`}
              style={{
                fill: theme.palette.text.primary,
                fontSize: 12,
                fontWeight: 700
              }}
            />
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </Box>
  );
};

export default RankingBarChart;